import { FC, useState, useEffect, Suspense, lazy } from 'react';
import clsx from 'clsx';

import { Types, useWindowSize, baseVariables } from '@marriott/mi-ui-library';
import { MerchandisingHeroBannerAssetProps } from './MerchandisingHeroBannerAsset/MerchandisingHeroBannerAsset.types';
import { MerchandisingHeroBannerSkeletonLoader } from './MerchandisingHeroBannerSkeletonLoader';
import {
  StyledMerchandisingHeroBannerContainer,
  StyledMerchandisingHeroBannerImage,
} from './MerchandisingHeroBanner.styles';
import { IMerchandisingHeroBanner } from './MerchandisingHeroBanner.types';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { URL_CONSTANTS, useMediaQuery } from '@marriott/shared/mi-helper-utils';
import { useSession } from '@marriott/mi-headless-utils';
import { MerchandisingHeroBannerVariations } from '../../molecules/MerchandisingHeroBannerVariations';
import {
  LocationCTA,
  MerchandisingHeroBanner_Primary_Secondary_Buttons,
} from '../../molecules/MerchandisingHeroBannerButtons';
import {
  getTrackingPropertiesDynamic,
  updateClass,
  getRecentlyViewedImageFallbackForPayload,
} from '../../utils/helpers';
import { MERCHHEROBANNER } from '../../utils/constants';
import {
  heroBannerDescriptionContent,
  MerchandisingHeroBannerContent,
} from '../../molecules/MerchandisingHeroBannerContent';
import { MerchImage } from '../../molecules/MerchImage/MerchImage';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

const MerchandisingHeroBannerVideo = lazy(() => import('./MerchandisingHeroBannerVideo/MerchandisingHeroBannerVideo'));
const MerchandisingHeroBannerAsset = lazy(() => import('./MerchandisingHeroBannerAsset/MerchandisingHeroBannerAsset'));

const { ImageAspectRatio, AssetVariation, Target, LinkTargetType, ScrimStyle, HeaderCopyWidth } = Types;
const { DEFAULT, EXTRATHIN, THIN, WIDE } = HeaderCopyWidth;
const { SIZE_16x9, SIZE_3x1, SIZE_2X1 } = ImageAspectRatio;
const { LIGHT_SCRIM } = ScrimStyle;
const { ENABLE_TARGET, DISABLE_TARGET } = Target;
const { VERTICAL_IMAGE } = AssetVariation;

export const MerchandisingHeroBanner: FC<IMerchandisingHeroBanner> = props => {
  const { mboxParameter, featureFlagLogo } = props;
  const {
    callToActionText1,
    destinationUrl1,
    primaryopeninanewtab,
    callToActionText2,
    destinationUrl2,
    secondaryopeninanewtab,
    caption,
    captionUrl,
    locationopeninanewtab,
    hideLocationCtaMobile,
    headerMedium,
    descriptionLong,
    eyebrowText,
    headertextstyle,
    heroSize,
    cobrandmathhero,
    mathLabel1,
    mathLabel2,
    mathLabel3,
    mathValue,
    mathType,
    assetsVariation,
    logo,
    font_icon,
    enableMaskingOnDesktop,
    imageLoading,
    styleclass,
    enableTarget,
    trackingProperties,
    opensInNewTab,
    playerId,
    videoaccountId,
    videoIdDesktop,
    videoIdMobile,
    videoEnabledDesktop = Boolean(videoIdDesktop?.trim()),
    videoEnabledMobile = Boolean(videoIdMobile?.trim()),
    enableAutoPlay,
    enableVideoLoop,
    enableMute,
    fileReferenceVideoCoverImage,
    playerDomain = URL_CONSTANTS.BRIGHTCOVE_PLAYER_DOMAIN,
    aspectRatioMobileVideo,
    fileReferenceImageVertical,
    verticalImageAltText,
    fontIconAltText,
    image,
    componentId,
    copyBlockWidth = DEFAULT,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    isTargetContentError = false,
    metaData,
    isHomepage = false, // if true then it is rendered on homepage
  } = props;
  const { width } = useWindowSize();
  const { datalayer } = useSession();

  const isLogoFlagEnabled = featureFlagLogo?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION];

  const recentlyViewedRectangleFallback = getRecentlyViewedImageFallbackForPayload(
    props as unknown as Record<string, unknown>,
    'rectangle'
  );
  const recentlyViewedStandardFallback = getRecentlyViewedImageFallbackForPayload(
    props as unknown as Record<string, unknown>,
    'standardHorizontal'
  );
  const recentlyViewedPanoramicFallback = getRecentlyViewedImageFallbackForPayload(
    props as unknown as Record<string, unknown>,
    'panoramicHorizontal'
  );

  const recentlyViewedClassicVerticalFallback = getRecentlyViewedImageFallbackForPayload(
    props as unknown as Record<string, unknown>,
    'classicVertical'
  );

  const { altText, dynamic } = image ?? {};
  let { assetPath, renditions } = image ?? {};

  if (metaData?.isRecentlyViewed === true && recentlyViewedRectangleFallback) {
    if (!renditions || renditions.length === 0) {
      assetPath = recentlyViewedRectangleFallback;
      renditions = [
        ...(recentlyViewedPanoramicFallback
          ? [
            {
              renditionPath: `${recentlyViewedPanoramicFallback}?wid=1440&hei=810, ${recentlyViewedPanoramicFallback}?wid=2880&hei=1620 2x`,
              mediaValue: '992px',
              width: 1440,
              dynamic: true,
              damPath: '',
              mediaQuery: 'min-width',
              height: 810,
            },
          ]
          : []),
        ...(recentlyViewedStandardFallback
          ? [
            {
              renditionPath: `${recentlyViewedStandardFallback}?wid=768&hei=432, ${recentlyViewedStandardFallback}?wid=1536&hei=864 2x`,
              mediaValue: '768px',
              width: 768,
              dynamic: true,
              damPath: '',
              mediaQuery: 'min-width',
              height: 432,
            },
          ]
          : []),
        ...(recentlyViewedClassicVerticalFallback
          ? [
            {
              renditionPath: `${recentlyViewedClassicVerticalFallback}?wid=365&hei=563, ${recentlyViewedClassicVerticalFallback}?wid=730&hei=1126 2x`,
              mediaValue: '767.5px',
              width: 365,
              dynamic: true,
              damPath: '',
              mediaQuery: 'max-width',
              height: 563,
            },
          ]
          : []),
      ];
    }
  }

  const [hasMobileVideoError, setHasMobileVideoError] = useState(false);
  const [hasDesktopVideoError, setHasDesktopVideoError] = useState(false);
  const showVideoOnDesktopOnly =
    videoEnabledDesktop && videoEnabledMobile && !hasDesktopVideoError && hasMobileVideoError;

  const showVideoOnMobileOnly =
    (videoEnabledDesktop && videoEnabledMobile && hasDesktopVideoError && !hasMobileVideoError) ||
    (!videoEnabledDesktop && videoEnabledMobile && !hasMobileVideoError);

  const showVideoOnDesktopAndMobile =
    (videoEnabledDesktop && !videoEnabledMobile && !hasDesktopVideoError) ||
    (videoEnabledDesktop && videoEnabledMobile && !hasDesktopVideoError && !hasMobileVideoError);

  const isVerticalImage = !!(assetsVariation && assetsVariation === VERTICAL_IMAGE && fileReferenceImageVertical);
  const isVerticalImageFromProps = !!(
    props.assetsVariation &&
    props.assetsVariation === VERTICAL_IMAGE &&
    props.fileReferenceImageVertical
  );
  const isCopyBlockWidthWide = !!(copyBlockWidth === WIDE);
  const isLightScrim = metaData?.theme ? metaData?.theme !== 'Dark' : styleclass?.includes(LIGHT_SCRIM);
  const shouldEnableDesktopMasking = enableMaskingOnDesktop && (heroSize === SIZE_16x9 || heroSize === SIZE_2X1);
  const isVariationsAvailable = mathType?.toLowerCase() === 'points' || mathType?.toLowerCase() === 'nights';
  const isHomepageHero2x1 = isHomepage && heroSize === SIZE_2X1;

  const copyBlockWidthContainerClass: string =
    heroSize === MERCHHEROBANNER.HERO3BY1
      ? 'col-12 col-md-7 col-lg-8 px-0 pr-sm-3 px-md-0'
      : copyBlockWidth === DEFAULT || (width && width < 576)
        ? 'col-12 col-xl-5 col-sm-6'
        : clsx('break-word col-12', {
          'col-xl-3 col-sm-4': copyBlockWidth === EXTRATHIN,
          'col-xl-4 col-sm-5': copyBlockWidth === THIN,
          'col-xl-6 col-sm-7': copyBlockWidth === WIDE && !isVerticalImage,
          'col-xl-7 col-lg-8 col-md-7 pr-lg-0 pr-xl-2': copyBlockWidth === WIDE && isVerticalImage,
        });

  const merchandisingHeroBannerAssetProps: MerchandisingHeroBannerAssetProps = {
    assetsVariation,
    fileReferenceImageVertical,
    verticalImageAltText,
    fontIcon: font_icon || '',
    fontIconAltText: fontIconAltText || '',
    heroSize,
    imgAltText: logo?.altText,
    logoImage: logo?.imageSrc,
    cobrandmathhero,
    isCopyBlockWidthWide,
    isLogoFlagEnabled,
    isUxlResponse: metaData ? true : false, // metaData object will only exist in uxl response so if uxl response is there then u should always use logo
  };

  // form custom click track value for hero cta's
  const formCustomClickTrackValue = (label: string, openInNewtab?: boolean): string => {
    const { INTERNAL, EXTERNAL } = LinkTargetType;
    if (metaData?.isRecentlyViewed) {
      return `${trackingProperties.trackingContentPosition}|${trackingProperties?.trackingDescription}|${primaryopeninanewtab ? EXTERNAL : INTERNAL
        }|event167,list3=${trackingProperties.trackingContentPosition};type=${trackingProperties?.trackingOfferType
        };msg=${trackingProperties?.trackingDescription};tag=${metaData?.merTag}`;
    }
    return trackingProperties?.clickTrack && !trackingProperties?.impressionTrack
      ? `${headerMedium ? headerMedium : descriptionLong}|${label}|${openInNewtab ? EXTERNAL : INTERNAL}`
      : '';
  };

  const descriptionClassName = clsx(
    heroSize === MERCHHEROBANNER.HERO3BY1 ? 'mb-0' : 'hb__subheading mb-0',
    isVerticalImage ? 'vertical__image__hero-' + heroSize?.replace(':', 'x') : '',
    isLightScrim ? '' : 't-color-inverse',
    heroSize === SIZE_16x9 || heroSize === SIZE_2X1
      ? ''
      : updateClass(' mt-1 mb-0', '', heroSize === MERCHHEROBANNER.HERO3BY1),
    (assetsVariation === AssetVariation.IMAGE && logo?.imageSrc && !cobrandmathhero) ||
      (assetsVariation === AssetVariation.FONT_ICON && font_icon && !cobrandmathhero)
      ? updateClass(' hb__subheading__assetsVariation', '', heroSize === MERCHHEROBANNER.HERO3BY1)
      : '',
    !isVerticalImage && cobrandmathhero
      ? updateClass(' hb__subheading__cobrandMathHero', '', heroSize === MERCHHEROBANNER.HERO3BY1)
      : '',
    heroSize === MERCHHEROBANNER.HERO3BY1 ? 'mb-0' : ''
  );

  useEffect(() => {
    window.addEventListener('load', () => {
      const parentContainer = document?.querySelector('.herobanner-fullbleed');
      parentContainer?.classList.remove('target-background');
      parentContainer?.classList.remove('herobanner-ratio-' + heroSize?.replace(':', 'x'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackingPropertiesDynamic = getTrackingPropertiesDynamic(
    trackingProperties,
    metaData,
    props,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isTargetContentError,
    props?.mboxParameter
  );

  /**
   * conditions for to render video
   */
  const isDesktopViewPort = useMediaQuery(baseVariables.mediaQuery.md);
  const shouldRenderDesktopVideo =
    (videoEnabledDesktop && !videoEnabledMobile && !hasDesktopVideoError) ||
    (videoEnabledDesktop && videoEnabledMobile && isDesktopViewPort && !hasDesktopVideoError);

  //Mobile video should render only when mobile video when enabled
  const shouldRenderMobileVideo = videoEnabledMobile && !isDesktopViewPort && !hasMobileVideoError;

  return (
    <StyledMerchandisingHeroBannerContainer
      data-content-type={isTargetContentLoaded && !isTargetContentError ? 'dcaContent' : 'evergreenContent'}
      data-variation={heroSize}
      data-testid={
        heroSize === MERCHHEROBANNER.HERO3BY1 ? 'merch-MerchandisingHeroBanner3X1' : 'MerchandisingHeroBannerLatest'
      }
      data-component-name={
        heroSize === MERCHHEROBANNER.HERO3BY1 ? 'merch-MerchandisingHeroBanner3X1' : 'o-merch-MerchandisingHeroBanner'
      }
      className={clsx(props.styleclass, {
        herobanner3X1: heroSize === MERCHHEROBANNER.HERO3BY1,
        hb__masking: shouldEnableDesktopMasking,
        'hb__video-enabled-desktop': showVideoOnDesktopAndMobile,
        'hb__video-enabled-desktop-only': showVideoOnDesktopOnly,
        'hb__video-enabled-mobile-only': showVideoOnMobileOnly,
        herobanner__2X1: heroSize === SIZE_2X1,
        herobanner__16X9: heroSize === SIZE_16x9,
        herobanner__3X1: heroSize === SIZE_3x1,
        herobanner3X1__3X1home: heroSize === MERCHHEROBANNER.HERO3BY1,
        'herobanner3X1__3X1home-variation': heroSize === MERCHHEROBANNER.HERO3BY1 && isVariationsAvailable,
      })}
      id={componentId}
      shouldClipPathFor2x1={!showVideoOnDesktopAndMobile && !showVideoOnDesktopOnly} //we use clip-path for 2x1 hero (for min-width: 768px) but we should not apply it when video is enabled on desktop
    >
      <Suspense fallback={''}>
        {!isTargetContentLoaded ? (
          <MerchandisingHeroBannerSkeletonLoader
            width={width}
            copyBlockWidth={props.copyBlockWidth} //explicily to be read from props as initial width of elements depends on initial props
            copyBlockWidthContainerClass={copyBlockWidthContainerClass}
            isVerticalImage={isVerticalImageFromProps}
            eyebrowText={props.eyebrowText}
            heroSize={props.heroSize}
            cobrandmathhero={props.cobrandmathhero}
            descriptionLong={props.descriptionLong}
            callToActionText2={props.callToActionText2}
            callToActionText1={props.callToActionText1}
            headerMedium={props.headerMedium}
            styleclass={props.styleclass}
            isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
          />
        ) : (
          <div
            className={clsx(
              'hero-banner',
              heroSize === MERCHHEROBANNER.HERO3BY1 ? '' : 'hb__scrim',
              enableTarget ? ENABLE_TARGET : DISABLE_TARGET,
              isLightScrim ? 'light-scrim' : 'dark-scrim'
            )}
          >
            <div
              className={clsx(
                heroSize === MERCHHEROBANNER.HERO3BY1 ? 'herobanner3X1__container' : 'hb-item',
                'hero-' + heroSize?.replace(':', 'x'),
                assetsVariation
              )}
            >
              <StyledMerchandisingHeroBannerImage>
                <MerchImage
                  customClass={clsx(
                    'heroBanner_background_image',
                    heroSize === MERCHHEROBANNER.HERO3BY1 ? 'herobanner3X1__img' : 'hb__img',
                    'd-block',
                    heroSize !== SIZE_16x9
                      ? updateClass('hb-3x1-ratio', 'herobanner3X1__img-3X1', heroSize === MERCHHEROBANNER.HERO3BY1)
                      : ''
                  )}
                  defaultImageURL={assetPath}
                  altText={altText}
                  dynamic={dynamic}
                  renditions={renditions}
                  title={altText || 'Hero Image'}
                  loading={imageLoading}
                  priority
                />
              </StyledMerchandisingHeroBannerImage>
              {Boolean(shouldRenderDesktopVideo || shouldRenderMobileVideo) &&
                !(heroSize === MERCHHEROBANNER.HERO3BY1) && (
                  <MerchandisingHeroBannerVideo
                    playerId={playerId}
                    videoaccountId={videoaccountId}
                    videoIdDesktop={videoIdDesktop}
                    videoIdMobile={videoIdMobile}
                    videoEnabledDesktop={videoEnabledDesktop}
                    videoEnabledMobile={videoEnabledMobile}
                    enableAutoPlay={enableAutoPlay}
                    enableVideoLoop={enableVideoLoop}
                    enableMute={enableMute}
                    fileReferenceVideoCoverImage={fileReferenceVideoCoverImage}
                    playerDomain={playerDomain}
                    location={headerMedium ? headerMedium : descriptionLong}
                    aspectRatioMobileVideo={aspectRatioMobileVideo}
                    clickTrack={trackingProperties?.clickTrack}
                    heroSize={heroSize}
                    hasDesktopVideoError={hasDesktopVideoError}
                    hasMobileVideoError={hasMobileVideoError}
                    setHasDesktopVideoError={setHasDesktopVideoError}
                    setHasMobileVideoError={setHasMobileVideoError}
                  />
                )}
              <div
                className={clsx(
                  updateClass(
                    'hb__cnt-sec',
                    'herobanner3X1__content variation-3X1 herobanner3X1__overlay pt-md-0',
                    heroSize === MERCHHEROBANNER.HERO3BY1
                  ),
                  'gradient',
                  !(isLightScrim || styleclass?.includes('no-scrim')) ? copyBlockWidth : '',
                  {
                    'overlay-text-hero3x1': heroSize === SIZE_3x1 && !(heroSize === MERCHHEROBANNER.HERO3BY1),
                  }
                )}
              >
                <div
                  className={clsx(
                    'container',
                    !(heroSize === MERCHHEROBANNER.HERO3BY1) &&
                    (isHomepageHero2x1 ? 'px-md-0 px-xl-2 d-md-flex d-xl-block' : 'px-md-2 py-md-0'),
                    !(heroSize === MERCHHEROBANNER.HERO3BY1) && 'hb__cnt-ctr hero-' + heroSize?.replace(':', 'x'),
                    heroSize === MERCHHEROBANNER.HERO3BY1 ? 'herobanner3X1__textcontainer p-sm-0 variation-3X1' : '',
                    heroSize === MERCHHEROBANNER.HERO3BY1
                      ? isVariationsAvailable
                        ? 'herobanner3X1__textcontainer-variation d-flex flex-column flex-xl-row'
                        : updateClass(
                          'herobanner3X1__textcontainer-standard',
                          'herobanner3X1__textcontainer-logo d-flex flex-column flex-xl-row',
                          logo?.imageSrc ? true : false
                        )
                      : '',
                    {
                      herobanner3X1__verticalimg: isVerticalImage,
                    },
                    {
                      vertical__image__Container: !(heroSize === MERCHHEROBANNER.HERO3BY1) && isVerticalImage,
                    }
                  )}
                >
                  {logo?.imageSrc && (
                    <MerchandisingHeroBannerAsset
                      {...merchandisingHeroBannerAssetProps}
                      isVariationsAvailable={isVariationsAvailable}
                      isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
                    />
                  )}
                  <div
                    className={clsx(
                      heroSize === MERCHHEROBANNER.HERO3BY1 ? 'herobanner3X1__fullwidth' : 'hb__text-overlay',
                      heroSize === MERCHHEROBANNER.HERO3BY1 ? copyBlockWidthContainerClass : '',
                      {
                        'col-12 col-lg-5 col-md-7 col-sm-12 d-flex flex-column text-center justify-content-center align-items-center d-md-block text-md-left':
                          isVerticalImage,
                        'flex-grow-1 pl-md-4 pl-xl-0': isHomepageHero2x1,
                      }
                    )}
                  >
                    <MerchandisingHeroBannerContent
                      assetsVariation={assetsVariation}
                      copyBlockWidthContainerClass={copyBlockWidthContainerClass}
                      logo={logo}
                      font_icon={font_icon}
                      eyebrowText={eyebrowText}
                      headerMedium={headerMedium}
                      headertextstyle={headertextstyle}
                      descriptionLong={descriptionLong}
                      heroSize={heroSize}
                      cobrandmathhero={cobrandmathhero}
                      isVerticalImage={isVerticalImage}
                      isLightScrim={isLightScrim}
                      isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
                      isVariationsAvailable={isVariationsAvailable}
                      isHomepage={isHomepage}
                    />
                    {isCopyBlockWidthWide &&
                      !isVerticalImage &&
                      isTargetContentLoaded &&
                      isVariationsAvailable &&
                      !(heroSize === MERCHHEROBANNER.HERO3BY1) && (
                        <MerchandisingHeroBannerVariations
                          dataLayer={datalayer as Record<string, unknown>}
                          isLightScrim={isLightScrim}
                          mathLabel1={mathLabel1}
                          mathLabel2={mathLabel2}
                          mathLabel3={mathLabel3}
                          mathValue={mathValue}
                          mathType={mathType}
                          isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
                          heroSize={heroSize}
                          isHomepage={isHomepage}
                        />
                      )}
                    {heroSize === MERCHHEROBANNER.HERO3BY1 && isVariationsAvailable && (
                      <div
                        className={clsx(
                          heroSize === MERCHHEROBANNER.HERO3BY1 && isVariationsAvailable
                            ? 'd-flex flex-column-reverse flex-xl-column'
                            : 'd-none'
                        )}
                      >
                        {isCopyBlockWidthWide && !isVerticalImage && isTargetContentLoaded && (
                          <MerchandisingHeroBannerVariations
                            dataLayer={datalayer as Record<string, unknown>}
                            isLightScrim={isLightScrim}
                            mathLabel1={mathLabel1}
                            mathLabel2={mathLabel2}
                            mathLabel3={mathLabel3}
                            mathValue={mathValue}
                            mathType={mathType}
                            isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
                            heroSize={heroSize}
                            isHomepage={isHomepage}
                          />
                        )}
                        {heroBannerDescriptionContent(
                          heroSize === MERCHHEROBANNER.HERO3BY1,
                          copyBlockWidthContainerClass,
                          descriptionLong,
                          descriptionClassName,
                          heroSize,
                          isHomepage
                        )}
                      </div>
                    )}
                    {/* Primary and Secondary Cta Button, if this condition satisfies then only render this component */}
                    {Boolean(callToActionText1 || (!isVerticalImage && callToActionText2)) && (
                      <MerchandisingHeroBanner_Primary_Secondary_Buttons
                        primaryopeninanewtab={primaryopeninanewtab}
                        secondaryopeninanewtab={secondaryopeninanewtab}
                        cobrandmathhero={cobrandmathhero}
                        heroSize={heroSize}
                        opensInNewTab={opensInNewTab}
                        trackingProperties={trackingProperties}
                        isVerticalImage={isVerticalImage}
                        callToActionText1={callToActionText1}
                        callToActionText2={callToActionText2}
                        destinationUrl1={destinationUrl1}
                        destinationUrl2={destinationUrl2}
                        metaData={metaData}
                        isTargetContentError={isTargetContentError}
                        isLightScrim={isLightScrim}
                        trackingPropertiesDynamic={trackingPropertiesDynamic}
                        custom_click_track_value={formCustomClickTrackValue(callToActionText2, secondaryopeninanewtab)}
                        isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
                        isVariationsAvailable={isVariationsAvailable}
                        isHomepage={isHomepage}
                      />
                    )}
                  </div>
                </div>
                {/* Location Cta Button */}
                {Boolean(caption) && (
                  <LocationCTA
                    locationopeninanewtab={locationopeninanewtab}
                    caption={caption}
                    hideLocationCtaMobile={hideLocationCtaMobile}
                    captionUrl={captionUrl}
                    compName={trackingProperties?.trackingContentPosition as string}
                    opensInNewTab={opensInNewTab}
                    custom_click_track_value={opensInNewTab}
                    isMerchHero3X1={heroSize === MERCHHEROBANNER.HERO3BY1 ? true : false}
                    isHomepageHero2x1={isHomepageHero2x1}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </Suspense>
    </StyledMerchandisingHeroBannerContainer>
  );
};
